import { Plus } from 'lucide-react';
import { useState } from 'react';
import { ServiceCard } from '@/components/services/ServiceCard';
import { ServiceForm } from '@/components/services/ServiceForm';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import type { ServiceDTO } from '@/features/services/api';
import {
  useActiveCnpjId,
  useServiceMutations,
  useServices,
} from '@/features/services/hooks';
import { ApiError } from '@/lib/api';

export default function ServicesPage() {
  const { cnpjId, isLoading: loadingCnpj } = useActiveCnpjId();
  const { data: services = [], isLoading } = useServices(cnpjId);
  const mutations = useServiceMutations(cnpjId);
  const toast = useToast();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceDTO | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ServiceDTO | null>(null);

  const openNew = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (svc: ServiceDTO) => {
    setEditing(svc);
    setFormOpen(true);
  };

  const handleDelete = async (svc: ServiceDTO) => {
    setConfirmDelete(null);
    try {
      await mutations.remove.mutateAsync(svc.id);
      toast.show({
        title: 'Serviço excluído',
        description: 'Você tem 5s para desfazer.',
        durationMs: 5000,
        action: {
          label: 'Desfazer',
          onClick: async () => {
            try {
              await mutations.restore.mutateAsync({
                cnpjId: svc.cnpjId,
                name: svc.name,
                color: svc.color,
                fixedShiftValue: svc.fixedShiftValue,
                tomadorCnpj: svc.tomadorCnpj,
                tomadorRazaoSocial: svc.tomadorRazaoSocial,
                tomadorEmail: svc.tomadorEmail,
                serviceCode: svc.serviceCode,
                issRetained: svc.issRetained,
                issAliquot: svc.issAliquot,
              });
            } catch (e) {
              toast.show({
                title: 'Não foi possível desfazer',
                description: e instanceof ApiError ? e.message : 'Tente novamente',
                variant: 'error',
              });
            }
          },
        },
      });
    } catch (e) {
      toast.show({
        title: 'Não foi possível excluir',
        description: e instanceof ApiError ? e.message : 'Tente novamente',
        variant: 'error',
      });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Serviços</h1>
          <p className="text-sm text-(--color-fg-muted)">
            Gerencie hospitais, clínicas e procedimentos contabilizáveis.
          </p>
        </div>
        <Button onClick={openNew} disabled={!cnpjId}>
          <Plus className="h-4 w-4" /> Novo serviço
        </Button>
      </header>

      {loadingCnpj || isLoading ? (
        <p className="text-sm text-(--color-fg-muted)">Carregando serviços…</p>
      ) : !cnpjId ? (
        <p className="text-sm text-(--color-fg-muted)">
          Nenhum CNPJ ativo. Cadastre um CNPJ em Configurações antes de criar serviços.
        </p>
      ) : services.length === 0 ? (
        <div className="rounded-md border border-dashed border-(--color-border) p-8 text-center">
          <p className="text-sm text-(--color-fg-muted)">
            Você ainda não cadastrou nenhum serviço neste CNPJ.
          </p>
          <Button className="mt-3" onClick={openNew}>
            <Plus className="h-4 w-4" /> Cadastrar primeiro serviço
          </Button>
        </div>
      ) : (
        <ul className="flex flex-col gap-2" aria-label="Lista de serviços">
          {services.map((svc) => (
            <li key={svc.id}>
              <ServiceCard
                service={svc}
                examCount={undefined}
                onEdit={() => openEdit(svc)}
                onDelete={() => setConfirmDelete(svc)}
              />
            </li>
          ))}
        </ul>
      )}

      {cnpjId ? (
        <ServiceForm
          open={formOpen}
          onOpenChange={setFormOpen}
          cnpjId={cnpjId}
          service={editing}
        />
      ) : null}

      <Dialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir serviço?</DialogTitle>
            <DialogDescription>
              Plantões antigos continuam acessíveis. Exames deste serviço ficam ocultos
              para novos lançamentos.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => confirmDelete && handleDelete(confirmDelete)}
            >
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
